// src/components/StatusSelectorModal.js
import React, { useEffect, useState, useMemo } from "react";
import "../styles/StatusSelectorModal.css";
import { useTranslation } from "react-i18next";

const StatusSelectorModal = ({
  isOpen,
  onClose,
  onSelect,
  currentStatus,
  type, // "PICKUP" | "DELIVERY" | "STRATEGICPOINT"
  locationName,
}) => {
  const { t } = useTranslation("status_selector_modal");
  const [selectionPath, setSelectionPath] = useState([]);

  // ✅ Labels via i18n
  const statusOptions = useMemo(() => {
    return {
      PENDING: t("statuses.PENDING"),

      ...(type === "PICKUP" && {
        IN_TRANSIT_MOVING_TO_PICKUP_LOCATION: t("statuses.IN_TRANSIT_MOVING_TO_PICKUP_LOCATION"),
        AT_PICKUP_LOCATION: t("statuses.AT_PICKUP_LOCATION"),
      }),

      ...(type === "DELIVERY" && {
        IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION: t("statuses.IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION"),
        AT_DELIVERY_LOCATION: t("statuses.AT_DELIVERY_LOCATION"),
      }),

      ...(type === "STRATEGICPOINT" && {
        COMPLETED: t("statuses.COMPLETED"),
        PENDING: t("statuses.PENDING"),
      }),

      COMPLETED: t("statuses.COMPLETED"),
      CANCELLED: t("statuses.CANCELLED"),
    };
  }, [t, type]);

  const subOptions = useMemo(() => {
    return {
      IN_TRANSIT_MOVING_TO_PICKUP_LOCATION: {
        MOVING_TO_PICKUP_LOCATION: t("subOptions.MOVING_TO_PICKUP_LOCATION"),
        DELAYED: t("subOptions.DELAYED"),
      },
      IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION: {
        MOVING_TO_DELIVERY_LOCATION: t("subOptions.MOVING_TO_DELIVERY_LOCATION"),
        DELAYED: t("subOptions.DELAYED"),
      },
      AT_PICKUP_LOCATION: {
        WAITING_TO_LOAD: t("subOptions.WAITING_TO_LOAD"),
        LOADING_IN_PROGRESS: t("subOptions.LOADING_IN_PROGRESS"),
      },
      AT_DELIVERY_LOCATION: {
        WAITING_TO_UNLOAD: t("subOptions.WAITING_TO_UNLOAD"),
        UNLOADING_IN_PROGRESS: t("subOptions.UNLOADING_IN_PROGRESS"),
      },
    };
  }, [t]);

  const subSubOptions = useMemo(() => {
    return {
      DELAYED: {
        DELAYED_BY_TRAFFIC: t("subSubOptions.DELAYED_BY_TRAFFIC"),
        DELAYED_BY_BREAKDOWN: t("subSubOptions.DELAYED_BY_BREAKDOWN"),
        DELAYED_BY_WEATHER: t("subSubOptions.DELAYED_BY_WEATHER"),
        DELAYED_BY_ROUTE_CHANGE: t("subSubOptions.DELAYED_BY_ROUTE_CHANGE"),
      },
      WAITING_TO_LOAD: {
        WAITING_FOR_MERCHANDISE: t("subSubOptions.WAITING_FOR_MERCHANDISE"),
        WAITING_DELAYED: t("subSubOptions.WAITING_DELAYED"),
      },
      LOADING_IN_PROGRESS: {
        LOADING_MERCHANDISE: t("subSubOptions.LOADING_MERCHANDISE"),
        LOADING_DELAYED: t("subSubOptions.LOADING_DELAYED"),
      },
      WAITING_TO_UNLOAD: {
        WAITING_FOR_UNLOADING: t("subSubOptions.WAITING_FOR_UNLOADING"),
        UNLOADING_DELAYED: t("subSubOptions.UNLOADING_DELAYED"),
      },
      UNLOADING_IN_PROGRESS: {
        UNLOADING_MERCHANDISE: t("subSubOptions.UNLOADING_MERCHANDISE"),
        UNLOADING_IN_PROGRESS_DELAYED: t("subSubOptions.UNLOADING_DELAYED"),
      },
    };
  }, [t]);

  const subSubSubOptions = useMemo(() => {
    return {
      WAITING_DELAYED: {
        WAITING_DELAYED_LATE_TRUCK: t("subSubSubOptions.WAITING_DELAYED_LATE_TRUCK"),
        WAITING_DELAYED_BREAKDOWN: t("subSubSubOptions.WAITING_DELAYED_BREAKDOWN"),
        WAITING_DELAYED_LOADING_ISSUE: t("subSubSubOptions.WAITING_DELAYED_LOADING_ISSUE"),
        WAITING_DELAYED_GOODS_NOT_READY: t("subSubSubOptions.WAITING_DELAYED_GOODS_NOT_READY"),
        WAITING_DELAYED_TRAILER_INCOMPATIBLE: t("subSubSubOptions.WAITING_DELAYED_TRAILER_INCOMPATIBLE"),
      },
      LOADING_DELAYED: {
        LOADING_DELAYED_DOCS: t("subSubSubOptions.LOADING_DELAYED_DOCS"),
        LOADING_DELAYED_SCHEDULING: t("subSubSubOptions.LOADING_DELAYED_SCHEDULING"),
        LOADING_DELAYED_EQUIPMENT: t("subSubSubOptions.LOADING_DELAYED_EQUIPMENT"),
        LOADING_DELAYED_STAFF: t("subSubSubOptions.LOADING_DELAYED_STAFF"),
        LOADING_DELAYED_INSPECTION: t("subSubSubOptions.LOADING_DELAYED_INSPECTION"),
      },
      UNLOADING_DELAYED: {
        UNLOADING_DELAYED_LATE_TRUCK: t("subSubSubOptions.UNLOADING_DELAYED_LATE_TRUCK"),
        UNLOADING_DELAYED_BREAKDOWN: t("subSubSubOptions.UNLOADING_DELAYED_BREAKDOWN"),
        UNLOADING_DELAYED_UNLOADING_ISSUE: t("subSubSubOptions.UNLOADING_DELAYED_UNLOADING_ISSUE"),
        UNLOADING_DELAYED_GOODS_NOT_READY: t("subSubSubOptions.UNLOADING_DELAYED_GOODS_NOT_READY"),
        UNLOADING_DELAYED_TRAILER_INCOMPATIBLE: t("subSubSubOptions.UNLOADING_DELAYED_TRAILER_INCOMPATIBLE"),
      },
      UNLOADING_IN_PROGRESS_DELAYED: {
        UNLOADING_DELAYED_DOCS: t("subSubSubOptions.UNLOADING_DELAYED_DOCS"),
        UNLOADING_DELAYED_SCHEDULING: t("subSubSubOptions.UNLOADING_DELAYED_SCHEDULING"),
        UNLOADING_DELAYED_EQUIPMENT: t("subSubSubOptions.UNLOADING_DELAYED_EQUIPMENT"),
        UNLOADING_DELAYED_STAFF: t("subSubSubOptions.UNLOADING_DELAYED_STAFF"),
        UNLOADING_DELAYED_INSPECTION: t("subSubSubOptions.UNLOADING_DELAYED_INSPECTION"),
      },
    };
  }, [t]);

  // 🔁 Update selectionPath when modal opens (same logic, no strings)
  useEffect(() => {
    if (!isOpen) return;

    setSelectionPath([]);

    setTimeout(() => {
      if (currentStatus in statusOptions) {
        setSelectionPath([currentStatus]);
      } else if (
        Object.keys(subOptions.IN_TRANSIT_MOVING_TO_PICKUP_LOCATION || {}).includes(currentStatus)
      ) {
        setSelectionPath(["IN_TRANSIT_MOVING_TO_PICKUP_LOCATION", currentStatus]);
      } else if (
        Object.keys(subOptions.IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION || {}).includes(currentStatus)
      ) {
        setSelectionPath(["IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION", currentStatus]);
      } else if (Object.keys(subOptions.AT_PICKUP_LOCATION || {}).includes(currentStatus)) {
        setSelectionPath(["AT_PICKUP_LOCATION", currentStatus]);
      } else if (Object.keys(subOptions.AT_DELIVERY_LOCATION || {}).includes(currentStatus)) {
        setSelectionPath(["AT_DELIVERY_LOCATION", currentStatus]);
      } else if (Object.keys(subSubOptions.DELAYED || {}).includes(currentStatus)) {
        if (type === "DELIVERY") {
          setSelectionPath(["IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION", "DELAYED", currentStatus]);
        } else {
          setSelectionPath(["IN_TRANSIT_MOVING_TO_PICKUP_LOCATION", "DELAYED", currentStatus]);
        }
      } else if (Object.keys(subSubOptions.WAITING_TO_LOAD || {}).includes(currentStatus)) {
        setSelectionPath(["AT_PICKUP_LOCATION", "WAITING_TO_LOAD", currentStatus]);
      } else if (Object.keys(subSubOptions.LOADING_IN_PROGRESS || {}).includes(currentStatus)) {
        setSelectionPath(["AT_PICKUP_LOCATION", "LOADING_IN_PROGRESS", currentStatus]);
      } else if (Object.keys(subSubOptions.WAITING_TO_UNLOAD || {}).includes(currentStatus)) {
        setSelectionPath(["AT_DELIVERY_LOCATION", "WAITING_TO_UNLOAD", currentStatus]);
      } else if (Object.keys(subSubOptions.UNLOADING_IN_PROGRESS || {}).includes(currentStatus)) {
        setSelectionPath(["AT_DELIVERY_LOCATION", "UNLOADING_IN_PROGRESS", currentStatus]);
      } else if (Object.keys(subSubSubOptions.WAITING_DELAYED || {}).includes(currentStatus)) {
        setSelectionPath(["AT_PICKUP_LOCATION", "WAITING_TO_LOAD", "WAITING_DELAYED", currentStatus]);
      } else if (Object.keys(subSubSubOptions.LOADING_DELAYED || {}).includes(currentStatus)) {
        setSelectionPath(["AT_PICKUP_LOCATION", "LOADING_IN_PROGRESS", "LOADING_DELAYED", currentStatus]);
      } else if (Object.keys(subSubSubOptions.UNLOADING_DELAYED || {}).includes(currentStatus)) {
        setSelectionPath(["AT_DELIVERY_LOCATION", "WAITING_TO_UNLOAD", "UNLOADING_DELAYED", currentStatus]);
      } else if (Object.keys(subSubSubOptions.UNLOADING_IN_PROGRESS_DELAYED || {}).includes(currentStatus)) {
        setSelectionPath(["AT_DELIVERY_LOCATION", "UNLOADING_IN_PROGRESS", "UNLOADING_IN_PROGRESS_DELAYED", currentStatus]);
      } else {
        setSelectionPath([]);
      }
    }, 0);
  }, [isOpen, currentStatus, type, statusOptions, subOptions, subSubOptions, subSubSubOptions]);

  const renderColumn = (options, levelKey) => (
    <div className={`status-options-column ${levelKey > 0 ? "animated-column" : ""}`}>
      {Object.entries(options).map(([value, label]) => {
        const isSelected = selectionPath[levelKey] === value;
        return (
          <button
            key={value}
            className={`status-option-btn ${isSelected ? "selected" : ""}`}
            onClick={() => {
              const isSameSelection = selectionPath[levelKey] === value;
              if (isSameSelection) return;

              if (levelKey === 0) {
                const hasChildren =
                  subOptions[value] || subSubOptions[value] || subSubSubOptions[value];

                if (hasChildren) {
                  setSelectionPath([]);
                  setTimeout(() => setSelectionPath([value]), 0);
                } else {
                  onSelect(value);
                  onClose();
                }
                return;
              }

              const nextSelectionPath = [...selectionPath.slice(0, levelKey), value];

              if (subSubSubOptions[value] || subSubOptions[value] || subOptions[value]) {
                setSelectionPath(nextSelectionPath);
              } else {
                onSelect(value);
                onClose();
              }
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const firstLevel = statusOptions;
  const secondLevel = subOptions[selectionPath[0]] || null;
  const thirdLevel = subSubOptions[selectionPath[1]] || null;
  const fourthLevel = subSubSubOptions[selectionPath[2]] || null;

  if (!isOpen) return null;

  const typeLabel =
    type === "PICKUP"
      ? t("types.pickup")
      : type === "DELIVERY"
      ? t("types.delivery")
      : t("types.strategicPoint");

  return (
    <div className="status-modal-backdrop">
      <div className="status-modal">
        <h3>{t("title")}</h3>

        <div className="status-location-type-line">
          {t("contextLine", { type: typeLabel })} <strong>{locationName}</strong>
        </div>

        <div className="status-columns-wrapper">
          {renderColumn(firstLevel, 0)}
          {secondLevel && renderColumn(secondLevel, 1)}
          {thirdLevel && renderColumn(thirdLevel, 2)}
          {fourthLevel && renderColumn(fourthLevel, 3)}
        </div>

        <button className="close-status-modal" onClick={onClose}>
          {t("buttons.cancel")}
        </button>
      </div>
    </div>
  );
};

export default StatusSelectorModal;
